import React from 'react';
import {
  Admin,
  ClientRenderer,
  ExpertRenderer,
  Guest,
  Logo,
  NotificationBanner,
} from 'layout/components/Header';
import withRelayEnvironment from 'core/withRelayEnvironment';
import { compose, QueryRendererContainer } from 'utils';
import { graphql } from 'react-relay';
import './Header.css';

const query = graphql`
  query HeaderQuery($name: String) {
    v2 {
      organisations(first: 1, name: $name) {
        edges {
          node {
            id
            name
            whitelabelImage
          }
        }
      }
    }
  }
`;

const Header = ({ user, whitelabelImageUrl, hideCta }) => {
  const role = user?.role ?? 'GUEST';
  const userFirstName = user?.firstName;
  const userLastName = user?.lastName;
  const userFullName = `${userFirstName} ${userLastName}`;
  const isProfileComplete = user?.consultant?.lifecycle?.basicProfileCompleted;
  const userProfileCode = user?.profileCode;
  const userProfileImg = user?.profilePicture?.source ?? null;
  const userVerificationLevel = user?.verificationLevel;
  const whitelabelImage = whitelabelImageUrl
    ? whitelabelImageUrl
    : user?.whitelabelImage;
  const isMarketplaceMember = user?.consultant?.lifecycle?.marketplaceMember;
  const isBainPrivateTalent =
    !isMarketplaceMember && user?.consultant?.memberOf?.id == 76137;
  const userOrganisationRole = user?.organisationRole;
  const isExploreEnabled = user?.isExploreEnabled;

  const templates = {
    guest: <Guest hideCta={hideCta} />,
    admin: <Admin userFirstName={userFirstName} userImage={userProfileImg} />,
    superadmin: (
      <Admin userFirstName={userFirstName} userImage={userProfileImg} />
    ),
    client: (
      <ClientRenderer
        userFirstName={userFirstName}
        userFullName={userFullName}
        userProfileCode={userProfileCode}
        userProfileImg={userProfileImg}
        userVerificationLevel={userVerificationLevel}
        userOrganisationRole={userOrganisationRole}
        isExploreEnabled={isExploreEnabled}
      />
    ),
    consultant: (
      <ExpertRenderer
        userFirstName={userFirstName}
        userFullName={userFullName}
        isProfileComplete={isProfileComplete}
        userProfileCode={userProfileCode}
        userProfileImg={userProfileImg}
        userVerificationLevel={userVerificationLevel}
        isMarketplaceMember={isMarketplaceMember}
      />
    ),
  };

  const showNotification =
    role.toLowerCase() === 'consultant' && isBainPrivateTalent;

  return (
    <div className={`react-nav ${showNotification ? 'with-notification' : ''}`}>
      <header className="l-header" role="banner">
        <div
          className={`l-header__main ${
            showNotification ? 'with-notification' : ''
          }`}
        >
          {showNotification ? (
            <div className="notification-banner">
              <div className="l-container-from-1020">
                <NotificationBanner />
              </div>
            </div>
          ) : null}
          <div className="l-header__inner l-container-from-1020">
            <a href="/" rel="home" className="l-header__logo">
              <div className="l-header__logo__inner">
                <Logo whitelabelImage={whitelabelImage} />
              </div>
            </a>
            {templates[role.toLowerCase()]}
          </div>
        </div>
        <noscript>
          <div
            className="c-feedback c-feedback--error is-visible
            u-text-align-center"
          >
            For the best experience on Expert360 we recommend you enable
            JavaScript. You can find instructions on how to enable JavaScript in
            your web browser
            <a
              href="http://www.enable-javascript.com/"
              rel="noopener noreferrer"
              target="_blank"
            >
              here
            </a>
            .
          </div>
        </noscript>
      </header>
    </div>
  );
};

const HeaderRenderer = ({ user, relayEnvironment }) => {
  const urlParams = new URLSearchParams(window.location.search);
  const invitingOrganisation = urlParams.get('invitingOrganisation');
  const isLogin =
    ['/login', '/sso-login'].indexOf(window.location.pathname) > -1;
  /* 
    show the white label image of the organisation if a
    marketplace user that was invited to join a private cloud
    is trying to login
  */
  if (isLogin && invitingOrganisation) {
    return (
      <QueryRendererContainer
        environment={relayEnvironment}
        query={query}
        variables={{ name: invitingOrganisation }}
        render={graph => {
          const whitelabelImage =
            graph?.v2?.organisations?.edges[0]?.node?.whitelabelImage;
          return (
            <Header
              user={user}
              whitelabelImageUrl={whitelabelImage}
              hideCta={true}
            ></Header>
          );
        }}
      />
    );
  } else {
    return <Header user={user}></Header>;
  }
};

export { Header };
export default compose(withRelayEnvironment)(HeaderRenderer);
